import React, { useState, useEffect, useRef } from 'react';
import { ChevronRight, ExternalLink, Trophy, Users, Server } from 'lucide-react';
import { i18n, tb, type Translations } from '../utils/i18n';

interface News {
  id: number;
  titleKey: keyof Translations;
  contentKey: keyof Translations;
  link?: string;
  type: 'news' | 'referral';
  gradient: string;
  icon: React.ReactNode;
}

interface NewsSliderProps {
  onNewsClick?: (news: News) => void;
  onNavigateToNews?: () => void;
}

const newsItems: News[] = [
  {
    id: 1,
    titleKey: 'boomEventTitle',
    contentKey: 'boomEventContent',
    type: 'news',
    gradient: 'from-blue-100 to-blue-200 dark:from-blue-900/30 dark:to-blue-800/30',
    icon: <Trophy className="w-6 h-6" />
  },
  {
    id: 2,
    titleKey: 'referralTitle',
    contentKey: 'referralContent',
    type: 'referral',
    gradient: 'from-green-100 to-green-200 dark:from-green-900/30 dark:to-green-800/30',
    icon: <Users className="w-6 h-6" />
  },
  {
    id: 3,
    titleKey: 'nodeTitle',
    contentKey: 'nodeContent',
    type: 'news',
    gradient: 'from-orange-100 to-orange-200 dark:from-orange-900/30 dark:to-orange-800/30',
    icon: <Server className="w-6 h-6" />
  }
];

export function NewsSlider({ onNewsClick, onNavigateToNews }: NewsSliderProps) {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [startX, setStartX] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [currentLanguage, setCurrentLanguage] = useState(i18n.getCurrentLanguage());
  const sliderRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % newsItems.length);
    }, 10000);

    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const checkLanguageChange = () => {
      const newLanguage = i18n.getCurrentLanguage();
      if (newLanguage !== currentLanguage) {
        setCurrentLanguage(newLanguage);
      }
    };

    const interval = setInterval(checkLanguageChange, 1000);
    return () => clearInterval(interval);
  }, [currentLanguage]);

  const currentNews = newsItems[currentIndex];

  const handleClick = () => {
    if (isDragging) return;

    if (currentNews.link) {
      window.open(currentNews.link, '_blank');
    } else if (onNavigateToNews) {
      window.location.hash = '#news';
      onNavigateToNews();
    } else if (onNewsClick) {
      onNewsClick(currentNews);
    }
  };

  const handleTouchStart = (e: React.TouchEvent) => {
    setStartX(e.touches[0].clientX);
    setIsDragging(false);
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    if (!startX) return;
    const currentX = e.touches[0].clientX;
    const diff = startX - currentX;

    if (Math.abs(diff) > 10) {
      setIsDragging(true);
    }
  };

  const handleTouchEnd = (e: React.TouchEvent) => {
    if (!startX) return;
    const endX = e.changedTouches[0].clientX;
    const diff = startX - endX;

    if (Math.abs(diff) > 50) {
      if (diff > 0) {
        setCurrentIndex((prev) => (prev + 1) % newsItems.length);
      } else {
        setCurrentIndex((prev) => (prev - 1 + newsItems.length) % newsItems.length);
      }
    }

    setStartX(0);
    setTimeout(() => setIsDragging(false), 100);
  };

  return (
    <div className="relative overflow-hidden">
      <div
        ref={sliderRef}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        className="touch-pan-x"
      >
        <button
          onClick={handleClick}
          className={`w-full bg-gradient-to-r ${currentNews.gradient} rounded-2xl p-4 transition-all duration-300 border border-gray-200 dark:border-gray-700 h-24 flex items-center`}
        >
          <div className="flex items-center space-x-3 w-full">
            <div className="w-12 h-12 bg-white/80 dark:bg-gray-800/80 rounded-full flex items-center justify-center text-gray-700 dark:text-gray-300">
              {currentNews.icon}
            </div>
            <div className="flex-1 text-left min-h-0">
              <h3 className="font-medium text-gray-800 dark:text-gray-200 mb-1 line-clamp-1">
                {tb(currentNews.titleKey)}
              </h3>
              <p className="text-gray-600 dark:text-gray-400 text-sm line-clamp-2 leading-tight">
                {tb(currentNews.contentKey)}
              </p>
            </div>
            <div className="flex items-center space-x-2 flex-shrink-0">
              {currentNews.link && (
                <ExternalLink className="w-4 h-4 text-gray-500 dark:text-gray-400" />
              )}
              <ChevronRight className="w-5 h-5 text-gray-500 dark:text-gray-400" />
            </div>
          </div>
        </button>
      </div>

      <div className="flex items-center justify-center space-x-1 mt-3">
        {newsItems.map((_, index) => (
          <button
            key={index}
            onClick={() => setCurrentIndex(index)}
            className={`w-2 h-2 rounded-full transition-all duration-300 ${
              index === currentIndex
                ? 'bg-primary w-6'
                : 'bg-muted-foreground/30 hover:bg-muted-foreground/50'
            }`}
          />
        ))}
      </div>
    </div>
  );
}
